import { ReactNode } from 'react';
import ReactApexChart from 'react-apexcharts';

import { Box, Theme, useTheme, Typography } from '@mui/material';

export type RadialBarColor = string | ((theme: Theme) => string);

export interface RadialBarThreshold {
  upTo: number;
  color: RadialBarColor;
}

export interface RadialBarProps {
  value: number | null;
  min?: number;
  max?: number;
  label?: ReactNode;
  thresholds?: RadialBarThreshold[];
  color?: RadialBarColor;
  trackColor?: RadialBarColor;
  size?: number;
  hollowSize?: string;
  ariaLabel?: string;
}

export function RadialBar({
  value,
  min = 0,
  max = 100,
  label,
  thresholds,
  color,
  trackColor,
  size = 120,
  hollowSize = '65%',
  ariaLabel,
}: RadialBarProps) {
  const theme = useTheme();

  const resolveColor = (c: RadialBarColor) => (typeof c === 'function' ? c(theme) : c);

  const clamped = value === null ? min : Math.min(Math.max(value, min), max);
  const percentage = value === null || max === min ? 0 : ((clamped - min) / (max - min)) * 100;

  const sortedThresholds = thresholds ? [...thresholds].sort((a, b) => a.upTo - b.upTo) : [];
  const barColor =
    value === null
      ? 'transparent'
      : resolveColor(
          sortedThresholds.find((t) => clamped <= t.upTo)?.color ??
            sortedThresholds[sortedThresholds.length - 1]?.color ??
            color ??
            theme.palette.primary.main
        );

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        hollow: { size: hollowSize },
        track: { background: trackColor ? resolveColor(trackColor) : theme.palette.divider },
        dataLabels: {
          name: { show: false },
          value: { show: false },
        },
      },
    },
    colors: [barColor],
    stroke: { lineCap: 'round' },
  };

  const defaultLabel =
    value === null ? '–' : Number.isInteger(value) ? value : value.toFixed(1);

  return (
    <Box
      sx={{ position: 'relative', width: size, height: size }}
      role="img"
      aria-label={ariaLabel ?? (value === null ? 'No data' : `${value} out of ${max}`)}
    >
      <ReactApexChart
        options={options}
        series={[percentage]}
        type="radialBar"
        width={size}
        height={size}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography fontWeight="bold" sx={{ fontSize: Math.round(size * 0.22) }}>
          {label ?? defaultLabel}
        </Typography>
      </Box>
    </Box>
  );
}
